/**
 * Step Editor Component
 * Modal for editing individual workflow steps
 * Includes Monaco editor for prompts and comprehensive form controls
 */

import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Info } from 'lucide-react';
import type { WorkflowStep, Provider, RetryStrategy } from '../types/volcano.types';
import { estimateStepCost, formatCostRange } from '../utils/cost-calculator';
import {
  getProviderModelList,
  getProviderName,
  getModelDisplayName,
  getDefaultModel,
} from '../utils/provider-config';
import { agentClient } from '@/lib/agentClient';

interface StepEditorProps {
  step: WorkflowStep;
  stepIndex: number;
  agentId: string;
  isOpen: boolean;
  onSave: (step: WorkflowStep) => Promise<void>;
  onCancel: () => void;
}

export default function StepEditor({
  step: initialStep,
  stepIndex,
  agentId,
  isOpen,
  onSave,
  onCancel,
}: StepEditorProps) {
  const [step, setStep] = useState<WorkflowStep>(initialStep);
  const [rawJson, setRawJson] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mcpServers, setMcpServers] = useState<Array<{ name: string; url: string }>>([]);
  const [loadingMcp, setLoadingMcp] = useState(false);

  // Load step data when modal opens
  useEffect(() => {
    if (isOpen) {
      const isLlmProvider = initialStep.provider === 'anthropic' || initialStep.provider === 'openai';
      // Normalize LLM defaults so optional fields don't crash the UI.
      const normalized: WorkflowStep = isLlmProvider
        ? ({
            mcps: [],
            timeout: 30000,
            retry: { strategy: 'exponential', attempts: 3 },
            prompt: '',
            ...initialStep,
          } as WorkflowStep)
        : initialStep;
      setStep(normalized);
      setRawJson(JSON.stringify(initialStep, null, 2));
      setError(null);
      loadMcpServers();
    }
  }, [isOpen, initialStep]);

  // Load available MCP servers
  const loadMcpServers = async () => {
    setLoadingMcp(true);
    try {
      const servers = await agentClient.getMcpServers();
      setMcpServers(servers.map(s => ({ name: s.name, url: s.url })));
    } catch (err) {
      console.error('Failed to load MCP servers:', err);
    } finally {
      setLoadingMcp(false);
    }
  };

  // Update step field
  const updateStep = <K extends keyof WorkflowStep>(
    field: K,
    value: WorkflowStep[K]
  ) => {
    setStep((prev) => ({ ...prev, [field]: value }));
  };

  // Update retry configuration
  const updateRetry = <K extends keyof WorkflowStep['retry']>(
    field: K,
    value: WorkflowStep['retry'][K]
  ) => {
    setStep((prev) => ({
      ...prev,
      retry: { ...prev.retry, [field]: value },
    }));
  };

  // Handle provider change (update model to default for new provider)
  const handleProviderChange = (provider: Provider) => {
    updateStep('provider', provider);
    updateStep('model', getDefaultModel(provider));
  };

  // Toggle MCP server selection
  const toggleMcpServer = (serverUrl: string) => {
    const mcps = step.mcps.includes(serverUrl)
      ? step.mcps.filter((url) => url !== serverUrl)
      : [...step.mcps, serverUrl];
    updateStep('mcps', mcps);
  };

  // Validate and save
  const handleSave = async () => {
    const isLlmProvider = step.provider === 'anthropic' || step.provider === 'openai';

    // Non-LLM steps: edited as raw JSON.
    if (!isLlmProvider) {
      let parsed: any;
      try {
        parsed = JSON.parse(rawJson);
      } catch {
        setError('Invalid JSON. Please fix syntax before saving.');
        return;
      }
      if (!parsed || typeof parsed !== 'object') {
        setError('Step JSON must be an object.');
        return;
      }
      if (!parsed.provider) {
        setError('Step JSON must include a provider field.');
        return;
      }
      setSaving(true);
      setError(null);
      try {
        await onSave(parsed as WorkflowStep);
      } catch (err: any) {
        setError(err.message || 'Failed to save step');
      } finally {
        setSaving(false);
      }
      return;
    }

    // LLM validation
    if (!String(step.prompt || '').trim()) {
      setError('Prompt cannot be empty');
      return;
    }
    if ((step.timeout ?? 0) <= 0) {
      setError('Timeout must be greater than 0');
      return;
    }
    if ((step.retry?.attempts ?? 0) <= 0) {
      setError('Retry attempts must be at least 1');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(step);
    } catch (err: any) {
      setError(err.message || 'Failed to save step');
    } finally {
      setSaving(false);
    }
  };

  // Calculate cost estimate
  const costEstimate = estimateStepCost(step);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Step {stepIndex + 1}</DialogTitle>
          <DialogDescription>
            Configure the step parameters, prompt, and execution settings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Non-LLM JSON Editor */}
          {step.provider !== 'anthropic' && step.provider !== 'openai' && (
            <div className="space-y-2">
              <Label htmlFor="step-json">Step JSON</Label>
              <textarea
                id="step-json"
                className="w-full font-mono text-xs border rounded p-3 h-[360px]"
                value={rawJson}
                onChange={(e) => setRawJson(e.target.value)}
              />
              <div className="text-xs text-gray-600">
                This step is edited as raw JSON because it is not an LLM provider step.
              </div>
            </div>
          )}

          {/* Step Name */}
          {(step.provider === 'anthropic' || step.provider === 'openai') && (
          <div className="space-y-2">
            <Label htmlFor="step-name">
              Step Name <span className="text-gray-500">(optional)</span>
            </Label>
            <Input
              id="step-name"
              placeholder={`Step ${stepIndex + 1}`}
              value={step.name || ''}
              onChange={(e) => updateStep('name', e.target.value)}
            />
          </div>
          )}

          {/* Provider Selection */}
          {(step.provider === 'anthropic' || step.provider === 'openai') && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="provider">Provider</Label>
              <Select
                value={step.provider}
                onValueChange={(value) => handleProviderChange(value as Provider)}
              >
                <SelectTrigger id="provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="anthropic">
                    {getProviderName('anthropic')}
                  </SelectItem>
                  <SelectItem value="openai">
                    {getProviderName('openai')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Model Selection */}
            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <Select
                value={step.model || getDefaultModel(step.provider)}
                onValueChange={(value) => updateStep('model', value)}
              >
                <SelectTrigger id="model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getProviderModelList(step.provider).map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          )}

          {/* Prompt Editor */}
          {(step.provider === 'anthropic' || step.provider === 'openai') && (
          <div className="space-y-2">
            <Label htmlFor="prompt">
              Prompt Template
              <span className="ml-2 text-xs text-gray-500">
                Use {'{{input}}'} and {'{{context.key}}'} for variables
              </span>
            </Label>
            <div className="border rounded-md overflow-hidden">
              <Editor
                height="200px"
                defaultLanguage="text"
                value={step.prompt || ''}
                onChange={(value) => updateStep('prompt', value || '')}
                options={{
                  minimap: { enabled: false },
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                  fontSize: 13,
                }}
                theme="vs-light"
              />
            </div>
            <div className="flex items-start gap-2 text-xs text-gray-600 bg-blue-50 p-2 rounded">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>
                Template syntax: Use <code>{'{{input}}'}</code> for user input and{' '}
                <code>{'{{context.keyName}}'}</code> for context variables from previous steps
              </p>
            </div>
          </div>
          )}

          {/* MCP Server Selection */}
          {(step.provider === 'anthropic' || step.provider === 'openai') && (
          <div className="space-y-2">
            <Label>
              MCP Tools <span className="text-gray-500">(optional)</span>
            </Label>
            <div className="border rounded-md p-3 space-y-2 bg-gray-50 max-h-[120px] overflow-y-auto">
              {loadingMcp ? (
                <div className="text-sm text-gray-600 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading MCP servers...
                </div>
              ) : mcpServers.length === 0 ? (
                <div className="text-sm text-gray-600">
                  No MCP servers registered
                </div>
              ) : (
                mcpServers.map((server) => (
                  <label
                    key={server.url}
                    className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={(step.mcps || []).includes(server.url)}
                      onChange={() => toggleMcpServer(server.url)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">{server.name}</span>
                    <span className="text-xs text-gray-500">{server.url}</span>
                  </label>
                ))
              )}
            </div>
            {(step.mcps || []).length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {(step.mcps || []).map((url) => {
                  const server = mcpServers.find((s) => s.url === url);
                  return (
                    <Badge key={url} variant="secondary">
                      {server?.name || url}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {/* Timeout Slider */}
          {(step.provider === 'anthropic' || step.provider === 'openai') && (
          <div className="space-y-2">
            <Label htmlFor="timeout">
              Timeout: {step.timeout / 1000}s
            </Label>
            <Slider
              id="timeout"
              min={1000}
              max={120000}
              step={1000}
              value={[step.timeout]}
              onValueChange={(value) => updateStep('timeout', value[0])}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>1s</span>
              <span>120s</span>
            </div>
          </div>
          )}

          {/* Retry Configuration */}
          {(step.provider === 'anthropic' || step.provider === 'openai') && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="retry-strategy">Retry Strategy</Label>
              <Select
                value={step.retry?.strategy}
                onValueChange={(value) =>
                  updateRetry('strategy', value as RetryStrategy)
                }
              >
                <SelectTrigger id="retry-strategy">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediate</SelectItem>
                  <SelectItem value="fixed">Fixed Delay</SelectItem>
                  <SelectItem value="exponential">Exponential Backoff</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="retry-attempts">Retry Attempts</Label>
              <Input
                id="retry-attempts"
                type="number"
                min={1}
                max={10}
                value={step.retry?.attempts}
                onChange={(e) =>
                  updateRetry('attempts', parseInt(e.target.value) || 1)
                }
              />
            </div>
          </div>
          )}

          {/* Cost Estimate */}
          {(step.provider === 'anthropic' || step.provider === 'openai') && (
          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-900">
                  Estimated Cost per Execution
                </div>
                <div className="text-xs text-gray-600">
                  Based on average token usage
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">
                  {formatCostRange(costEstimate.min, costEstimate.max)}
                </div>
                <div className="text-xs text-gray-500">
                  Provider: {getProviderName(step.provider)}
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Step'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

